<template>
    <div v-if="isLoading" class="user-badges user-badges--loading" data-testid="user-badges-loading">
        <v-skeleton-loader type="chip" />
    </div>

    <div
        v-else-if="hasAnything"
        :class="['user-badges', compact ? 'user-badges--compact' : 'user-badges--full']"
        data-testid="user-badges"
    >
        <StreakIndicator
            v-if="showStreakIndicator"
            :current-streak="currentStreak"
            :longest-streak="longestStreak"
            :size="streakSize"
            :gender="gender"
        />

        <BadgeChip
            v-for="badge in visibleBadges"
            :key="badge.key"
            :badge="badge"
            :size="badgeSize"
            :gender="gender"
            :current-streak="currentStreak"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useBadgesForUser } from '../../composition/useBadgesForUser';
import { compareBadges, isProgressionBadge } from '../../utils/badgeCatalog';
import { BadgeTier, type ResolvedUserBadge } from '../../models/Badge';
import { type Gender } from '../../models/User';
import BadgeChip from './BadgeChip.vue';
import StreakIndicator from './StreakIndicator.vue';

type Props = {
    userId: string;
    compact?: boolean;
    badgeSize?: number;
    /**
     * Gender of the *displayed* user, used to render their badge copy in the
     * right form (e.g. another user's "Junakinja" instead of the viewer's
     * "Junak"). When omitted the children fall back to the viewer's own
     * preference — correct for the "my profile" surfaces.
     */
    gender?: Gender;
};

const props = withDefaults(defineProps<Props>(), {
    compact: false,
    badgeSize: undefined,
    gender: undefined,
});

const { badges, currentStreak, longestStreak, isLoading, refresh } = useBadgesForUser(
    () => props.userId,
);

const TIER_RANK: Record<BadgeTier, number> = {
    [BadgeTier.Wood]: 0,
    [BadgeTier.Bronze]: 1,
    [BadgeTier.Silver]: 2,
    [BadgeTier.Gold]: 3,
};

const visibleBadges = computed<ResolvedUserBadge[]>(() => {
    const resolved = badges.value ?? [];

    if (!props.compact) {
        return resolved.slice().sort((a, b) => compareBadges(a.definition, b.definition));
    }

    const best = pickHighestBadge(resolved);
    return best ? [best] : [];
});

// Full mode always renders the indicator (even at 0) so brand-new users see
// what they're aiming at, with the tooltip explaining the first milestone.
// Compact mode (UserMenu) only shows it once the user actually has a streak —
// a "0" next to the top-badge chip would crowd the menu without adding signal.
const showStreakIndicator = computed(
    () => !props.compact || currentStreak.value >= 1,
);

const hasAnything = computed(
    () => visibleBadges.value.length > 0 || showStreakIndicator.value,
);

const badgeSize = computed(() => {
    if (props.badgeSize != null) {
        return props.badgeSize;
    }

    return props.compact ? 22 : 48;
});

const streakSize = computed(() => badgeSize.value);

function pickHighestBadge(list: ResolvedUserBadge[]): ResolvedUserBadge | undefined {
    if (list.length === 0) {
        return undefined;
    }

    return list.reduce<ResolvedUserBadge>((best, candidate) => {
        return rankFor(candidate) > rankFor(best) ? candidate : best;
    }, list[0]);
}

function rankFor(badge: ResolvedUserBadge): number {
    if (!isProgressionBadge(badge.definition)) {
        return Number.MAX_SAFE_INTEGER;
    }

    return TIER_RANK[badge.definition.tier];
}

defineExpose({ refresh });
</script>

<style lang="scss" scoped>
.user-badges {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    line-height: 0;
}

.user-badges--full {
    justify-content: center;
    margin-block: 4px;
}

.user-badges--compact {
    gap: 2px;
}
</style>
